import { useHistory } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowRight, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createCampaign, fetchCampaigns } from 'api/campaigns';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';

const GeneralDonationsSettings = () => {
    const toast = useToast();
    const history = useHistory();
    const { accountId } = useCurrentAccount();

    const { data: generalCampaign } = useSuspenseQuery({
        queryKey: accountQueryKeys.campaigns(accountId),
        queryFn: () => fetchCampaigns(accountId, { excl_type: 'external' }),
        select: data => data.data.find(campaign => campaign.type === 'general'),
    });

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: () => {
            return createCampaign(accountId, {
                type: 'general',
                title: 'General Donations',
                enable_team_fundraising: false,
                enable_auction: false,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.campaigns(accountId),
                type: 'all',
            });
            toast.success('Campaign created successfully!');
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>General Donations</SettingsCard.Heading>
                <p className="text-sm text-gray-700">
                    A campaign to accept general donations on your landing page and website.
                </p>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                {generalCampaign?.id ? (
                    <Button
                        variant="solid"
                        size="lg"
                        className="self-start !border-sky-100 !bg-sky-100 !text-sky-700"
                        onClick={() => {
                            generalCampaign?.id
                                ? history.push(
                                      `/accounts/${accountId}/campaigns/${generalCampaign.id}/settings`,
                                  )
                                : mutate();
                        }}
                    >
                        Manage Campaign
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                ) : (
                    <Button
                        variant="solid"
                        size="lg"
                        className="self-start !border-sky-100 !bg-sky-100 !text-sky-700"
                        onClick={() => mutate()}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <FontAwesomeIcon className="animate-spin" icon={faSpinnerThird} />
                        ) : null}
                        Enable General Donations
                    </Button>
                )}
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { GeneralDonationsSettings };
